import { useRestApiProvider } from "@jugl-web/rest-api";
import React, { useCallback, useRef, useState } from "react";
import { WhatsappAgentLogModel } from "@jugl-web/rest-api/integrations/models";
import { useEffectOnce } from "react-use";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import {
  DataLoadingWrapper,
  EmptyListContent,
  TableGrid,
} from "@jugl-web/ui-kit";
import {
  humanizeCustomerPhoneNumber,
  isoToLocalDate,
  useTranslations,
} from "@jugl-web/utils";
import format from "date-fns/format";
import { AccountLogDetailsDialog } from "./components/AccountLogDetailsDialog";
import { AccountLogStatusPill } from "./components/AccountLogStatusPill";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";

type PageState = {
  pageNumber: number;
  requestId: number;
  isError?: boolean;
  hasMore?: boolean;
};

export const WhatsappLogsHistoryTab: React.FC = () => {
  const { integrationsApi } = useRestApiProvider();
  const { t } = useTranslations();
  const [getAgentLogs, { isError, isLoading, isFetching, data }] =
    integrationsApi.useLazyGetAgentLogsQuery();
  const currentPageState = useRef<PageState | null>(null);
  const { entityId } = useEntitySelectedProvider();
  const [agentLogs, setAgentLogs] = useState<WhatsappAgentLogModel[]>([]);
  const [selectedAgentLog, setSelectedAccountLog] =
    useState<WhatsappAgentLogModel>();

  const loadNextPage = useCallback(async () => {
    let nextPageNumber = 1;
    if (currentPageState.current) {
      if (currentPageState.current.isError) {
        nextPageNumber = currentPageState.current.pageNumber;
      } else {
        nextPageNumber = currentPageState.current.pageNumber + 1;
      }
    }

    const nextPageState: PageState = {
      requestId: Math.random(),
      pageNumber: nextPageNumber,
    };

    currentPageState.current = nextPageState;

    const result = await getAgentLogs({
      entityId,
      page: nextPageNumber,
      page_size: 10,
    });

    if (currentPageState.current?.requestId !== nextPageState.requestId) {
      return;
    }

    if ("error" in result && result.error) {
      nextPageState.isError = true;
      currentPageState.current = nextPageState;
      return;
    }

    if ("data" in result && result.data && result.data.data) {
      const responseData = result.data.data;
      setAgentLogs((prev) =>
        nextPageNumber === 1 ? responseData : [...prev, ...responseData]
      );

      nextPageState.hasMore = result.data.total_pages > nextPageNumber;
      currentPageState.current = nextPageState;
    }
  }, [entityId, getAgentLogs]);

  useEffectOnce(() => {
    loadNextPage();
  });

  return (
    <>
      <DataLoadingWrapper
        onRetry={loadNextPage}
        isError={isError}
        isLoading={
          isLoading ||
          !data ||
          (isFetching && currentPageState.current?.pageNumber === 1)
        }
        isFetching={isFetching}
      >
        {agentLogs.length ? (
          <TableGrid
            data={agentLogs}
            id="client-support-logs"
            onReachEnd={() =>
              currentPageState.current?.hasMore && !isLoading && !isFetching
                ? loadNextPage()
                : undefined
            }
            inline
            variant="grid"
            onRowClick={setSelectedAccountLog}
            columns={[
              {
                id: "action-trigger",
                title: t({
                  id: "client-support-page.action-trigger",
                  defaultMessage: "Action Trigger",
                }),
                grow: true,
                className: "font-medium leading-[140%] tracking-[0.16px]",
                content: ({ trigger }) => <>{trigger}</>,
              },
              {
                id: "date",
                title: t({
                  id: "client-support-page.date",
                  defaultMessage: "Date",
                }),
                grow: true,
                className: "text-primary-800 underline",
                content: ({ inserted_at }) => (
                  <>
                    {format(isoToLocalDate(inserted_at), "d MMM yyyy, h:mm a")}
                  </>
                ),
              },
              {
                id: "update-sent-to",
                title: t({
                  id: "client-support-page.update-sent-to",
                  defaultMessage: "Update Sent to",
                }),
                grow: true,
                content: ({ recipient_name, mobile }) => (
                  <div className="flex flex-col gap-0.5">
                    <span>{recipient_name}</span>
                    <span className="text-[#828282]">
                      {humanizeCustomerPhoneNumber(mobile)}
                    </span>
                  </div>
                ),
              },
              {
                id: "account-id",
                title: t({
                  id: "client-support-page.account-id",
                  defaultMessage: "Account ID",
                }),
                grow: true,
                content: ({ waba_id }) => <>{waba_id}</>,
              },
              {
                id: "status",
                title: t({
                  id: "client-support-page.status",
                  defaultMessage: "Status",
                }),
                grow: true,
                content: ({ status }) => (
                  <AccountLogStatusPill status={status} />
                ),
              },
            ]}
          />
        ) : (
          <EmptyListContent
            type="custom"
            className="w-[500px]"
            customImg={<WhatsappIcon />}
            customTitle={t({
              id: "client-support-page.logs-history-is-empty",
              defaultMessage: "Logs History is empty yet",
            })}
            customSubtitle={t({
              id: "client-support-page.logs-history-description",
              defaultMessage:
                "Here you will find activity logs, capturing when all task update or message template was sent to the Customers",
            })}
          />
        )}
      </DataLoadingWrapper>
      <AccountLogDetailsDialog
        isOpen={!!selectedAgentLog}
        onRequestClose={() => setSelectedAccountLog(undefined)}
        accountLog={selectedAgentLog}
      />
    </>
  );
};
